import React, { useState } from 'react';
import classNames from 'classnames';

import Field, { hasDataInFields } from '../../Field';

import SectionContainer from '../SectionContainer';
import css from './SectionTestimonials.module.css';
import { IconPlay, Modal } from '../../../../components';
import { YoutubeEmbed } from './../../Primitives/YoutubeEmbed/YoutubeEmbed';

import img1 from './images/1.png';
import img2 from './images/2.png';
import img3 from './images/3.png';
import pattern from './images/pattern.svg';

export const extractYouTubeID = url => {
  const regExp = /(?:youtube\.com\/(?:[^\/\n\s]+\/\S+\/|(?:v|e(?:mbed)?|shorts|live)\/|\S*?[?&]v=)|youtu\.be\/)([a-zA-Z0-9_-]{11})/i;
  const match = url ? url.match(regExp) : null;

  return match ? match[1] : null;
};

// The number of columns (numColumns) affects styling and responsive images
const COLUMN_CONFIG = [
  { css: css.oneColumn, responsiveImageSizes: '(max-width: 767px) 100vw, 1200px' },
  { css: css.twoColumns, responsiveImageSizes: '(max-width: 767px) 100vw, 600px' },
  { css: css.threeColumns, responsiveImageSizes: '(max-width: 767px) 100vw, 400px' },
  { css: css.fourColumns, responsiveImageSizes: '(max-width: 767px) 100vw, 265px' },
];
const getIndex = numColumns => numColumns - 1;
const getColumnCSS = numColumns => {
  const config = COLUMN_CONFIG[getIndex(numColumns)];
  return config ? config.css : COLUMN_CONFIG[0].css;
};
const getResponsiveImageSizes = numColumns => {
  const config = COLUMN_CONFIG[getIndex(numColumns)];
  return config ? config.responsiveImageSizes : COLUMN_CONFIG[0].responsiveImageSizes;
};

/**
 * @typedef {Object} BlockConfig
 * @property {string} blockId
 * @property {string} blockName
 * @property {'defaultBlock' | 'footerBlock' | 'socialMediaLink'} blockType
 */

/**
 * @typedef {Object} FieldComponentConfig
 * @property {ReactNode} component
 * @property {Function} pickValidProps
 */

/**
 * Section component that's able to show blocks in multiple different columns (defined by "numColumns" prop)
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to components own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {Object} props.defaultClasses
 * @param {string} props.defaultClasses.sectionDetails
 * @param {string} props.defaultClasses.title
 * @param {string} props.defaultClasses.description
 * @param {string} props.defaultClasses.ctaButton
 * @param {string} props.sectionId id of the section
 * @param {'columns'} props.sectionType
 * @param {Object?} props.title
 * @param {Object?} props.description
 * @param {Object?} props.appearance
 * @param {Object?} props.callToAction
 * @param {Array<BlockConfig>?} props.blocks array of block configs
 * @param {boolean?} props.isInsideContainer
 * @param {Object} props.options extra options for the section component (e.g. custom fieldComponents)
 * @param {Object<string,FieldComponentConfig>?} props.options.fieldComponents custom fields
 * @returns {JSX.Element} Section for article content
 */
const SectionTestimonials = props => {
  const {
    sectionId,
    className,
    rootClassName,
    defaultClasses,
    numColumns = 3,
    title,
    description,
    appearance,
    callToAction,
    blocks = [],
    isInsideContainer = false,
    options,
    intl,
    onManageDisableScrolling,
  } = props;

  // If external mapping has been included for fields
  // E.g. { h1: { component: MyAwesomeHeader } }
  const fieldComponents = options?.fieldComponents;
  const fieldOptions = { fieldComponents };

  const hasHeaderFields = hasDataInFields([title, description, callToAction], fieldOptions);
  const hasBlocks = blocks?.length > 0;

  const testimonials = [
    {
      img: {
        src: img1,
        alt: 'img1',
      },
      videoLink: 'https://www.youtube.com/watch?v=AY5qcIq5u2g',
    },
    {
      img: {
        src: img2,
        alt: 'img2',
      },
      videoLink: 'https://www.youtube.com/watch?v=AY5qcIq5u2g',
    },
    {
      img: {
        src: img3,
        alt: 'img3',
      },
      videoLink: 'https://www.youtube.com/watch?v=AY5qcIq5u2g',
    },
  ];

  const secondaryCtaData = {
    fieldType: 'internalButtonLink',
    href: '/p/free-test',
    content: 'Free test',
  };

  const [currentTestimonialIndex, setCurrentTestimonialIndex] = useState(0);
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
  const currentVideoLink = testimonials[currentTestimonialIndex]?.videoLink;
  const currentVideo = extractYouTubeID(currentVideoLink);

  return (
    <SectionContainer
      id={sectionId}
      className={className}
      rootClassName={rootClassName}
      appearance={appearance}
      options={fieldOptions}
    >
      {hasHeaderFields ? (
        <header className={classNames(css.sectionDetails, defaultClasses.sectionDetails)}>
          <Field data={title} className={defaultClasses.title} options={fieldOptions} />
          <Field data={description} className={defaultClasses.description} options={fieldOptions} />
        </header>
      ) : null}
      <div
        className={classNames(
          css.testimonials,
          defaultClasses.blockContainer,
          getColumnCSS(numColumns),
          {
            [css.noSidePaddings]: isInsideContainer,
          }
        )}
      >
        {testimonials.map((t, index) => (
          <div className={css.testimonialContainer}>
            <div
              className={css.testimonial}
              onClick={() => {
                setCurrentTestimonialIndex(index);
                setIsVideoModalOpen(true);
              }}
            >
              <img className={css.image} alt={t.img.alt} src={t.img.src} />
              <div className={css.iconPlay}>
                <IconPlay />
              </div>
            </div>
          </div>
        ))}
      </div>
      <img className={css.pattern} src={pattern} alt="pattern" />
      <header className={classNames(css.ctas, defaultClasses.sectionDetails)}>
        <div className={css.ctaContainer}>
          <Field
            data={callToAction}
            className={classNames(defaultClasses.ctaButton, css.ctaButton)}
            options={fieldOptions}
          />
          <Field
            data={secondaryCtaData}
            className={classNames(defaultClasses.ctaButton, css.ctaButton, css.secondaryCta)}
            options={fieldOptions}
          />
        </div>
      </header>

      <Modal
        id="SectionTestimonials.video"
        contentClassName={css.videoModal}
        isOpen={isVideoModalOpen}
        onClose={() => setIsVideoModalOpen(false)}
        usePortal
        onManageDisableScrolling={onManageDisableScrolling}
      >
        <h2 className={css.modalTitle}>
          {intl.formatMessage({ id: 'LandingPage.SectionTestimonials.videoTitle' })}
        </h2>
        <p className={css.modalDescription}>
          {intl.formatMessage({ id: 'LandingPage.SectionTestimonials.videoDescription' })}
        </p>
        <YoutubeEmbed youtubeVideoId={currentVideo} />
      </Modal>
    </SectionContainer>
  );
};

export default SectionTestimonials;
